import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of, throwError } from 'rxjs';
import { map, catchError, finalize } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import { ProjectsService } from './api/projects.service';

export interface ChecklistOption {
  key: string;
  value: string;
  checklistId: string;
  lastRevision: boolean;
  jurisdictionId?: string | number;
}

export interface CacheEntry {
  data: ChecklistOption[];
  timestamp: number;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root'
})
export class ChecklistCacheService {
  private cache = new Map<string, CacheEntry>();
  private pendingRequests = new Map<string, Observable<ChecklistOption[]>>();
  
  private readonly CACHE_TTL = 5 * 60 * 1000;
  
  private cacheUpdated$ = new BehaviorSubject<string | null>(null);

  constructor(private projectsService: ProjectsService) {}

  getChecklistsByJurisdictions(jurisdictionIds: number[]): Observable<ChecklistOption[]> {
    if (jurisdictionIds.length === 0) {
      return this.getAllChecklists();
    }

    const cacheKey = this.createCacheKey(jurisdictionIds);
    
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      return of(cached);
    }
    
    const pending = this.pendingRequests.get(cacheKey);
    if (pending) {
      return pending;
    }

    const request$ = this.createJurisdictionRequest(jurisdictionIds).pipe(
      map(result => {
        this.setCache(cacheKey, result);
        return result;
      }),
      catchError(error => {
        console.error('Error loading checklists for jurisdictions:', jurisdictionIds, error);
        return throwError(() => error);
      }),
      finalize(() => {
        this.pendingRequests.delete(cacheKey);
      })
    );
    
    this.pendingRequests.set(cacheKey, request$);
    return request$;
  }

  getAllChecklists(): Observable<ChecklistOption[]> {
    const cacheKey = 'all_checklists';
    
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      return of(cached);
    }
    
    const pending = this.pendingRequests.get(cacheKey);
    if (pending) {
      return pending;
    }
    
    const params = new HttpParams().set('size', '9999');
    const request$ = this.projectsService.listSettingsChecklist({ params }).pipe(
      map((response: any) => {
        const options = response.data.checklists.map((checklist: any) => ({
          key: checklist.checklistId,
          value: checklist.name,
          checklistId: checklist.checklistId,
          lastRevision: checklist.lastRevision,
          jurisdictionId: checklist.jurisdiction?.jurisdictionId
        }));
        this.setCache(cacheKey, options);
        return options;
      }),
      catchError(error => {
        console.error('Error loading all checklists:', error);
        return throwError(() => error);
      }),
      finalize(() => {
        this.pendingRequests.delete(cacheKey);
      })
    );
    
    this.pendingRequests.set(cacheKey, request$);
    return request$;
  }

  clearCache(): void {
    this.cache.clear();
    this.pendingRequests.clear();
    this.cacheUpdated$.next(null);
  }

  invalidateCacheEntry(jurisdictionIds: number[]): void {
    const cacheKey = this.createCacheKey(jurisdictionIds);
    this.cache.delete(cacheKey);
    this.pendingRequests.delete(cacheKey);
  }

  getCacheUpdates(): Observable<string | null> {
    return this.cacheUpdated$.asObservable();
  }

  getCacheStats(): { totalEntries: number; pendingRequests: number; cacheKeys: string[] } {
    return {
      totalEntries: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      cacheKeys: Array.from(this.cache.keys())
    };
  }

  private createJurisdictionRequest(jurisdictionIds: number[]): Observable<ChecklistOption[]> {
    const uncachedIds: number[] = [];
    const cachedResults = new Map<string, ChecklistOption>();
    
    jurisdictionIds.forEach(id => {
      const singleIdKey = this.createCacheKey([id]);
      const cached = this.getFromCache(singleIdKey);
      if (cached) {
        cached.forEach(option => cachedResults.set(option.key, option));
      } else {
        uncachedIds.push(id);
      }
    });
    
    if (uncachedIds.length === 0) {
      return of(Array.from(cachedResults.values()));
    }
    
    const promises = uncachedIds.map(jurisdictionId => {
      const queryParams = new HttpParams()
        .set('size', '9999')
        .set('jurisdictionId', jurisdictionId.toString());
      
      return this.projectsService.listSettingsChecklist({ params: queryParams }).toPromise();
    });

    return new Observable(observer => {
      Promise.all(promises).then((responses: any[]) => {
        responses.forEach((response, index) => {
          if (response && response.data && response.data.checklists) {
            const jurisdictionId = uncachedIds[index];
            const singleIdKey = this.createCacheKey([jurisdictionId]);
            const checklistsForJurisdiction: ChecklistOption[] = [];
            
            response.data.checklists.forEach((checklist: any) => {
              const option = {
                key: checklist.checklistId,
                value: checklist.name,
                checklistId: checklist.checklistId,
                lastRevision: checklist.lastRevision,
                jurisdictionId: checklist.jurisdiction?.jurisdictionId
              };
              cachedResults.set(option.key, option);
              checklistsForJurisdiction.push(option);
            });
            
            this.setCache(singleIdKey, checklistsForJurisdiction);
          }
        });
        
        observer.next(Array.from(cachedResults.values()));
        observer.complete();
      }).catch(error => {
        observer.error(error);
      });
    });
  }

  private createCacheKey(jurisdictionIds: number[]): string {
    return jurisdictionIds.sort((a, b) => a - b).join(',');
  }

  private getFromCache(key: string): ChecklistOption[] | null {
    const entry = this.cache.get(key);
    if (!entry) {
      return null;
    }
    
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data;
  }

  private setCache(key: string, data: ChecklistOption[]): void {
    const now = Date.now();
    const entry: CacheEntry = {
      data,
      timestamp: now,
      expiresAt: now + this.CACHE_TTL
    };
    
    this.cache.set(key, entry);
    this.cacheUpdated$.next(key);
  }
}